import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDocSheetOpen } from '../../../utils/docSheetPresence';
import { stampDocOverlays } from '../../../utils/variableScope';
import { collectAttachedDocIds } from '../utils/resetCampaignProgress';
import { fetchDocsById } from './useSprintDetailViews/resetCampaignHelpers';

// One in-flight (or finished) fetch per attached-doc set. Effect restarts —
// campaign list updates, strict-mode remounts — join this instead of GETting
// every doc again.
const overlayDocFetches = new Map();

function fetchOverlayDocs(docIds) {
  const key = docIds.slice().sort().join('\0');
  let job = overlayDocFetches.get(key);
  if (!job) {
    job = fetchDocsById(docIds).then((docs) => {
      if (docs.size === 0) overlayDocFetches.delete(key);
      return docs;
    });
    overlayDocFetches.set(key, job);
  }
  return job;
}

/**
 * Campaign variables for docs: live scope, ancestor hydration, and immediate persist.
 * Ancestor campaigns in `allSprints` are often list-metadata only (no `variables`).
 * Inheritance (panel + doc pills) needs those rows — load a parent when the
 * variables panel or a doc sheet actually needs them, not on every nested open.
 */
export default function useCampaignVariables({
  localSprint,
  setLocalSprint,
  allSprints,
  loadFullSprint,
  onUpdateSprint,
  isMultiCampaignMode,
}) {
  const [isVariablesPanelOpen, setIsVariablesPanelOpen] = useState(false);
  const docSheetOpen = useDocSheetOpen();
  const needsAncestorVariables = isVariablesPanelOpen || docSheetOpen;

  const allSprintsRef = useRef(allSprints);
  allSprintsRef.current = allSprints;

  const localSprintRef = useRef(localSprint);
  localSprintRef.current = localSprint;

  const stampedOverlaySprintsRef = useRef(new Set());

  // Value copies saved before `docOverlay` existed sit on the campaign next
  // to real campaign variables. Mark them so nested campaigns stop listing
  // them under the parent campaign's name.
  useEffect(() => {
    if (!needsAncestorVariables || !loadFullSprint || isMultiCampaignMode) return undefined;
    let cancelled = false;
    (async () => {
      const start = localSprintRef.current;
      if (!start?.id) return;
      const chainIds = [];
      const visited = new Set();
      let cursor = start;
      while (cursor?.id && !visited.has(cursor.id)) {
        visited.add(cursor.id);
        chainIds.push(cursor.id);
        const parentId = cursor.parentSprintId;
        cursor = parentId
          ? allSprintsRef.current.find((s) => s.id === parentId)
          : null;
      }
      for (const id of chainIds) {
        if (cancelled || stampedOverlaySprintsRef.current.has(id)) continue;
        let sprint =
          id === localSprintRef.current?.id
            ? localSprintRef.current
            : allSprintsRef.current.find((s) => s.id === id);
        if (!sprint) continue;
        if (!Array.isArray(sprint.tasks) || !Array.isArray(sprint.variables)) {
          const loaded = await loadFullSprint(id);
          if (cancelled) return;
          sprint = loaded || allSprintsRef.current.find((s) => s.id === id);
        }
        if (!sprint || !Array.isArray(sprint.tasks) || !Array.isArray(sprint.variables)) {
          continue;
        }
        const hasUnmarked = sprint.variables.some((v) => v && !v.docOverlay && v.id);
        if (!hasUnmarked) {
          stampedOverlaySprintsRef.current.add(id);
          continue;
        }
        const docIds = collectAttachedDocIds(sprint.tasks);
        if (docIds.length === 0) {
          stampedOverlaySprintsRef.current.add(id);
          continue;
        }
        const docsById = await fetchOverlayDocs(docIds);
        if (cancelled) return;
        if (docsById.size === 0) continue;
        const fromList = allSprintsRef.current.find((s) => s.id === id);
        const base =
          id === localSprintRef.current?.id && Array.isArray(localSprintRef.current?.tasks)
            ? localSprintRef.current
            : fromList && Array.isArray(fromList.tasks)
              ? fromList
              : sprint;
        if (!Array.isArray(base.tasks)) continue;
        const { variables, changed } = stampDocOverlays(
          base.variables || [],
          [...docsById.values()].map((doc) => doc.variables)
        );
        stampedOverlaySprintsRef.current.add(id);
        if (!changed) continue;
        const next = { ...base, variables };
        if (localSprintRef.current?.id === id) {
          localSprintRef.current = next;
          setLocalSprint(next);
        }
        onUpdateSprint?.(next, { immediate: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    needsAncestorVariables,
    localSprint?.id,
    localSprint?.parentSprintId,
    loadFullSprint,
    isMultiCampaignMode,
    onUpdateSprint,
    setLocalSprint,
  ]);

  useEffect(() => {
    if (!needsAncestorVariables) return undefined;
    if (!loadFullSprint || isMultiCampaignMode || !localSprint?.parentSprintId) return undefined;
    let cancelled = false;
    (async () => {
      const visited = new Set();
      let parentId = localSprint.parentSprintId;
      while (parentId && !visited.has(parentId) && !cancelled) {
        visited.add(parentId);
        const ancestor = allSprintsRef.current.find((s) => s.id === parentId);
        if (!ancestor) break;
        if (!Object.prototype.hasOwnProperty.call(ancestor, 'variables')) {
          // Already fully loaded (pre-variables model) — treat as no vars to inherit
          if (Array.isArray(ancestor.tasks)) {
            parentId = ancestor.parentSprintId || null;
            continue;
          }
          const loaded = await loadFullSprint(parentId);
          if (cancelled) return;
          parentId = loaded?.parentSprintId || ancestor.parentSprintId || null;
        } else {
          parentId = ancestor.parentSprintId || null;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    needsAncestorVariables,
    localSprint?.id,
    localSprint?.parentSprintId,
    loadFullSprint,
    isMultiCampaignMode,
  ]);

  // LIVE campaign scope: docs from missions resolve pills against this, then up parentSprintId.
  const variableCampaign = useMemo(
    () =>
      localSprint
        ? {
            id: localSprint.id,
            name: localSprint.name,
            variables: localSprint.variables || [],
            parentSprintId: localSprint.parentSprintId || null,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localSprint?.id, localSprint?.name, localSprint?.variables, localSprint?.parentSprintId]
  );

  const handleCampaignVariablesChange = useCallback(
    (variables) => {
      // Functional update so a mission-value clear queued earlier in this
      // click is the base. Replacing from localSprintRef would put those
      // filled This mission values back — the ref only catches up on render.
      let next = null;
      flushSync(() => {
        setLocalSprint((current) => {
          const base = current || localSprintRef.current;
          if (!base) return current;
          next = { ...base, variables };
          localSprintRef.current = next;
          return next;
        });
      });
      if (!next) return;
      // Bypass useSprintSync's 500ms debounce + the 2s save throttle —
      // variables must hit the API as soon as the panel flushes (survives F5).
      onUpdateSprint?.(next, { immediate: true });
    },
    [setLocalSprint, onUpdateSprint]
  );

  /** Patch a variable that lives on an ancestor (or current) campaign. */
  const handleInheritedVariablePatch = useCallback(
    async (sourceId, variableId, patch) => {
      if (!sourceId || !variableId || !onUpdateSprint) return;
      let source = allSprintsRef.current.find((s) => s.id === sourceId);
      // Prefer the live local sprint when patching the campaign we're viewing —
      // its variables are fresher than the allSprints list snapshot.
      const local = localSprintRef.current;
      if (local?.id === sourceId) source = local;
      if (!source) return;
      // Must save a full sprint — metadata-only updates would wipe tasks.
      if (!Array.isArray(source.tasks)) {
        const loaded = loadFullSprint ? await loadFullSprint(sourceId) : null;
        source = loaded || allSprintsRef.current.find((s) => s.id === sourceId);
        if (!source || !Array.isArray(source.tasks)) return;
      }
      const vars = source.variables || [];
      if (!vars.some((v) => v.id === variableId)) return;
      const next = {
        ...source,
        variables: vars.map((v) => (v.id === variableId ? { ...v, ...patch } : v)),
      };
      if (local?.id === sourceId) {
        localSprintRef.current = next;
        setLocalSprint(next);
      }
      onUpdateSprint(next, { immediate: true });
    },
    [loadFullSprint, onUpdateSprint, setLocalSprint]
  );

  const handleCampaignVariablesColumnWidthsChange = useCallback(
    (variablesColumnWidths) => {
      const prev = localSprintRef.current;
      if (!prev) return;
      const next = { ...prev, variablesColumnWidths };
      localSprintRef.current = next;
      setLocalSprint(next);
      onUpdateSprint?.(next, { immediate: true });
    },
    [setLocalSprint, onUpdateSprint]
  );

  return {
    isVariablesPanelOpen,
    setIsVariablesPanelOpen,
    variableCampaign,
    handleCampaignVariablesChange,
    handleInheritedVariablePatch,
    handleCampaignVariablesColumnWidthsChange,
  };
}
